import { sentryAddBreadcrumb } from 'utils/sentry';
import { v4 } from 'uuid';

export enum DataObjectType {
    Mention = 'mention',
    File = 'file',
    Plot = 'plot',
}

export const MentionTriggerChar = '@';
export const DataObjectTypeAttributeName = 'data-object';
export const DataObjectKeyAttributeName = 'data-object-key';
export const DataObjectIdAttributeName = 'data-object-id';
export const DataMentionInputAttributeName = 'data-mention-input';

const TagGuid = '[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}';
const TagOpen = '[{]{2}';
const TagClose = '[}]{2}';

const EmbedTagRegex = new RegExp(
    `${TagOpen}(mention|file):(${TagGuid})${TagClose}`,
    'gm',
);
const WhiteSpaceRegex = /\s/;

export type ContentEditableTokenBase = Readonly<{
    key: string;
    startIndex: number;
    endIndex: number;
}>;

export type ContentEditableTextToken = ContentEditableTokenBase &
    Readonly<{
        type: 'text';
        text: string;
    }>;

export type ContentEditableMentionToken = ContentEditableTokenBase &
    Readonly<{
        type: DataObjectType.Mention;
        id: string;
    }>;

export type ContentEditableFileToken = ContentEditableTokenBase &
    Readonly<{
        type: DataObjectType.File;
        id: string;
    }>;

export type ContentEditableToken =
    | ContentEditableTextToken
    | ContentEditableMentionToken
    | ContentEditableFileToken;

function parseEmbedTokens(text: string): ContentEditableToken[] {
    return Array.from(text.matchAll(EmbedTagRegex)).map<ContentEditableToken>((match) => {
        if (match.length !== 3) {
            throw new Error('Missing match details');
        }

        const type = match[1] as DataObjectType;

        const shared = {
            key: `${type}-${match.index}`,
            startIndex: match.index,
            endIndex: match.index + match[0].length - 1,
            id: match[2],
        };

        switch (type) {
            case DataObjectType.Mention:
                return {
                    type: DataObjectType.Mention,
                    ...shared,
                };
            case DataObjectType.File:
                return {
                    type: DataObjectType.File,
                    ...shared,
                };
            default:
                throw new Error('Invalid embed type');
        }
    });
}

export function parseContentEditableTokens(text: string): ContentEditableToken[] {
    const lookup = new Map<number, ContentEditableToken>();

    parseEmbedTokens(text).forEach((token) => lookup.set(token.startIndex, token));

    const tokens: ContentEditableToken[] = [];
    let index = 0;
    let buffer = '';
    let startIndex = 0;

    const addTextTokenFromBuffer = () => {
        if (buffer) {
            tokens.push({
                type: 'text',
                key: `text-${startIndex}`,
                startIndex,
                endIndex: index - 1,
                text: buffer,
            });
            buffer = '';
        }
    };

    while (index < text.length) {
        const token = lookup.get(index);
        if (token) {
            addTextTokenFromBuffer();
            tokens.push(token);
            index = token.endIndex + 1;
            startIndex = index;
        } else {
            buffer += text[index];
            index += 1;
        }
    }

    addTextTokenFromBuffer();

    return tokens;
}

export function buildEmbedNode(id: string, type: DataObjectType) {
    const embedNode = document.createElement('span');
    embedNode.setAttribute(DataObjectKeyAttributeName, v4());
    embedNode.setAttribute(DataObjectTypeAttributeName, type);
    embedNode.setAttribute(DataObjectIdAttributeName, id);
    embedNode.setAttribute('contentEditable', 'false');
    return embedNode;
}

export function createEmbedNodes(id: string, type: DataObjectType) {
    const embedNode = buildEmbedNode(id, type);
    const textNode = document.createTextNode(' ');
    return [embedNode, textNode];
}

export function createSpinnerNode() {
    const spinnerNode = document.createElement('span');
    spinnerNode.setAttribute('contenteditable', 'false');
    spinnerNode.setAttribute('role', 'status');
    spinnerNode.setAttribute('aria-label', 'Uploading image');

    return spinnerNode;
}

export function buildTokenNode(token: ContentEditableToken) {
    switch (token.type) {
        case 'text':
            return document.createTextNode(token.text);
        case 'mention':
        case 'file':
            return buildEmbedNode(token.id, token.type);
        default:
            throw new Error('Invalid token type');
    }
}

export function deserializeContentEditableText(text: string): Node[] {
    return parseContentEditableTokens(text).map(buildTokenNode);
}

export function serializeEmbed(id: string, type: DataObjectType) {
    return `{{${type}:${id}}}`;
}

export function serializeContentEditableText(input: HTMLElement): string {
    return Array.from<ChildNode>(input.childNodes).reduce<string>((result, node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return result + (node.textContent ?? '');
        }
        if (node instanceof HTMLSpanElement) {
            const objectType = node.getAttribute(DataObjectTypeAttributeName);

            if (
                objectType === DataObjectType.Mention ||
                objectType === DataObjectType.File
            ) {
                const id = node.getAttribute(DataObjectIdAttributeName);
                if (!id) {
                    throw new Error(`Missing ${objectType} id`);
                }
                return `${result}${serializeEmbed(id, objectType)}`;
            }

            return `${result}${serializeContentEditableText(node)}`;
        }
        if (node instanceof HTMLDivElement) {
            return `${result}\n${serializeContentEditableText(node)}`;
        }
        return result;
    }, '');
}

export function isMentionTriggered(event: Event, range: Range) {
    if (!(event instanceof InputEvent)) {
        return false;
    }

    // inputType can be undefined in some browsers/events despite TypeScript's types
    const inputType = event.inputType as string | undefined;
    if (inputType === undefined) {
        sentryAddBreadcrumb({
            category: 'input.mention',
            message: 'InputEvent.inputType is undefined in isMentionTriggered',
            level: 'warning',
        });
        return false;
    }

    return (
        inputType === 'insertText' &&
        event.data === MentionTriggerChar &&
        range.startContainer.textContent !== null &&
        range.endContainer.textContent !== null &&
        // has whitespace or nothing before the selection
        (range.startOffset === 1 ||
            range.startContainer.textContent
                .substring(range.startOffset - 2, range.startOffset - 1)
                .match(WhiteSpaceRegex)) &&
        // has whitespace or nothing after the selection
        (range.endOffset === range.endContainer.textContent.length ||
            range.endContainer.textContent
                .substring(range.endOffset, range.endOffset + 1)
                .match(WhiteSpaceRegex))
    );
}

export function insertSearchElement(caretRange: Range) {
    const input = document.createElement('span');
    input.setAttribute(DataMentionInputAttributeName, '');
    input.textContent = MentionTriggerChar;
    input.autocapitalize = 'off';
    input.spellcheck = false;
    input.contentEditable = 'true';

    // Replace at mention symbol with a content editable span for search
    caretRange.setStart(caretRange.startContainer, caretRange.startOffset - 1);
    caretRange.deleteContents();
    caretRange.insertNode(input);
    caretRange.setEnd(input, 1);
    caretRange.collapse();

    return input;
}
